import React, { useState } from 'react';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import * as Icons from '@mui/icons-material';
import Header from './Header';

const DRAWER_WIDTH = 240;

const DashboardLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { text: 'Tableau de bord', icon: <Icons.Dashboard />, path: '/dashboard' },
    { text: 'Administrateurs', icon: <Icons.People />, path: '/admin-users' },
    { text: 'Utilisateurs', icon: <Icons.Group />, path: '/users' },
    { text: 'Inscriptions', icon: <Icons.PersonAdd />, path: '/registrations' },
    { text: 'Retours', icon: <Icons.Feedback />, path: '/feedback' },
    { text: 'Rôles', icon: <Icons.Security />, path: '/roles' },
    { text: 'Prompts', icon: <Icons.Code />, path: '/prompts' },
    { text: 'Historique des corrections', icon: <Icons.History />, path: '/correction-history' },
    { text: 'Abonnements', icon: <Icons.Subscriptions />, path: '/subscriptions' },
    { text: 'Paramètres', icon: <Icons.Settings />, path: '/settings' },
    { text: 'Statistiques', icon: <Icons.BarChart />, path: '/statistics' },
    { text: 'Mots clés', icon: <Icons.Label />, path: '/keywords-admin' },
  ];

  const drawer = (
    <Box sx={{ mt: 2 }}>
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            component={Link}
            to={item.path}
            key={item.text}
            selected={location.pathname === item.path}
            sx={{
              mx: 1,
              borderRadius: 1,
              '&.Mui-selected': {
                backgroundColor: 'rgba(51, 122, 183, 0.08)', // Very light blue/pastel
                color: '#337ab7', // Matching blue for text
                '&:hover': {
                  backgroundColor: 'rgba(51, 122, 183, 0.12)',
                },
                '& .MuiListItemIcon-root': {
                  color: '#337ab7', // Matching blue for icon
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
              },
            }}
          >
            <ListItemIcon 
              sx={{ 
                color: location.pathname === item.path ? '#337ab7' : 'text.secondary',
                minWidth: 40
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: location.pathname === item.path ? 600 : 400
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Header>
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <Icons.Menu />
          </IconButton>
        )}
      </Header>

      <Box
        component="nav"
        sx={{
          width: { sm: DRAWER_WIDTH },
          flexShrink: { sm: 0 }
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              bgcolor: 'background.paper',
              borderRight: '1px solid',
              borderColor: 'divider'
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              bgcolor: 'background.paper',
              borderRight: '1px solid',
              borderColor: 'divider',
              top: '64px',
              height: 'calc(100% - 64px)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: '64px',
          bgcolor: '#f5f5f5', // Light gray background for main content
          '& .MuiPaper-root': { // Add some depth to cards/papers in the main content
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            borderRadius: 1
          }
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default DashboardLayout; 